import { pipe, find, propOr, reduce, groupBy, flatten, propEq, filter, complement, either } from 'ramda';
import map from 'lodash/fp/map';
import uniqBy from 'lodash/uniqBy';
import compact from 'lodash/fp/compact';
import sortBy from 'lodash/fp/sortBy';
import { createSelector } from 'reselect';
import { PAYMENT_ICON } from './constants';
import { PAYMENT_TYPE } from '../../main/payment/constants';

const isPayout = propEq('type', 'payout');
const isRecurringMembership = propEq('type', 'recurring_membership');
const isSalesTransaction = complement(either(isPayout, isRecurringMembership));
export const getTransactions = state => state.register.summary.refundList;
export const getDailySummarySales = createSelector(
  getTransactions,
  filter(isSalesTransaction)
);
export const getDailySummaryPayouts = createSelector(
  getTransactions,
  filter(isPayout)
);
export const getRecurringMemberships = createSelector(
  getTransactions,
  filter(isRecurringMembership)
);

export const getDailySummaryNumberOfTickets = state => state.register.summary.numberOfTickets;
export const getDailySummaryTotals = state => state.register.summary.totals;


export const isLoading = state => state.register.summary.isLoading;

const extractBarberFromSaleFn = (memo, item) => {
  if (item.barber) {
    return [...memo, { ...item.barber, appointmentTime: item.appointmentTime }];
  }

  return [...memo, item.barber];
};

const barberGroupMapFn = (group) => {
  const hasAppointment = group.some(b => b.appointmentTime);

  if (!hasAppointment) {
    return uniqBy(group, 'id');
  }

  return group.filter(g => g.appointmentTime);
};

const extractAndGroupBarbers = pipe(reduce(extractBarberFromSaleFn, []), groupBy(b => b.id));
const uniqBarbers = pipe(extractAndGroupBarbers, map(barberGroupMapFn), flatten);

export const mapUsersToSale = map((sale) => {
  const customers = sale.items.reduce((memo, item) => [...memo, item.customer], []);

  return ({
    ...sale,
    barbers: uniqBarbers(sale.items),
    customers: uniqBy(customers, 'id'),
  });
});

export const collectDescription = map((sale) => {
  const description = sale.items
    .reduce((memo, item) => [...memo, item.description], [])
    .join('\n');

  return ({
    ...sale,
    description,
  });
});

const makeCardInfo = (item) => {
  const cardInfo = [item.cardInfo];
  if (item.type !== 'payout' && item.type !== 'recurring_membership') {
    item.payments.map(payment => payment.giftCardCode && cardInfo.push(`GC Number: ${payment.giftCardCode}`));
  }
  return compact(cardInfo);
};

const makePaymentIcons = (item) => {
  if (item.type === 'payout') {
    return [PAYMENT_ICON.payout];
  }
  if (item.type === 'recurring_membership') {
    return [PAYMENT_ICON.card];
  }
  const payments = item.payments ? item.payments.map(payment => payment.type) : [];
  const isGiftCardOnlyPayment = PAYMENT_TYPE.giftCard === item.paymentType &&
                                payments.includes(PAYMENT_TYPE.giftCard) &&
                                payments.length === 1;
  if (item.isViaOnline && !isGiftCardOnlyPayment) {
    const isGiftCardPayment = PAYMENT_TYPE.giftCard === item.paymentType || payments.includes(PAYMENT_TYPE.giftCard);
    return [PAYMENT_ICON.isViaOnline].concat(isGiftCardPayment ? PAYMENT_ICON[PAYMENT_TYPE.giftCard] : []);
  }

  return compact(sortBy('type', payments).map(paymentType => PAYMENT_ICON[paymentType]));
};

const findAppointment = find(item => item.type === 'appointment');

export const mapPaymentStatus = map(sale => ({
  ...sale,
  cardInfo: makeCardInfo(sale.items[0]),
  icons: makePaymentIcons(sale.items[0]),
  shouldShowReceipt: true,
  time: propOr(sale.time, 'time', findAppointment(sale.items)),
  appointmentTime: propOr(null, 'appointmentTime', findAppointment(sale.items)),
  items: sale.items.map(item => ({
    ...item,
    parentId: sale.id,
    method: item.paymentType === 'cash' ? 'Cash' : 'POS',
    status: item.chargeRefundId ? 'Refunded' : 'Paid',
    cardInfo: makeCardInfo(item),
    icons: makePaymentIcons(item),
    shouldShowReceipt: sale.items.length <= 1,
  })),
}));

const mapPaymentStatusForPayout = map(payout => ({
  ...payout,
  cardInfo: makeCardInfo(payout),
  icons: makePaymentIcons(payout),
}));

const mapRecurringMembership = map(recurringMembership => ({
  ...recurringMembership,
  cardInfo: makeCardInfo(recurringMembership),
  icons: makePaymentIcons(recurringMembership),
  customer: {
    lastName: recurringMembership.subscriberName,
  },
  maxRefundAmount: recurringMembership.refundable ? recurringMembership.totalCost : 0,
  refundedCost: recurringMembership.refundable ? 0 : recurringMembership.cost,
  refundedTax: recurringMembership.refundable ? 0 : recurringMembership.tax,
  totalCost: recurringMembership.refundable ? recurringMembership.totalCost : 0,
  status: recurringMembership.refundable ? 'Paid' : 'Refunded',
}));

export const getDailySummary = createSelector(
  getDailySummarySales,
  getDailySummaryPayouts,
  getRecurringMemberships,
  (sales, payouts, recurringMemberships) => ([
    ...pipe(mapUsersToSale, collectDescription, mapPaymentStatus)(sales),
    ...mapPaymentStatusForPayout(payouts),
    ...mapRecurringMembership(recurringMemberships),
  ])
);
